<template>
	<div class="flex-center flex-column">
		<div
			class="align-end justify-center w-smm-100 pb-8 pt-2 px-md-10 text-md-h5 text-body-5 white--text hidden-sm-and-down"
			style="flex-wrap: wrap; border: 1px solid #fefefe30; border-radius: 5px; display: flex">
			<div v-for="(token, index) in tokens" :key="index" class="d-flex align-end justify-end">
				<span v-if="token.type === 'text'" class="text-md-h3 text-h6 mb-1 mx-1">
					{{ token.value }}
				</span>
				<div v-else-if="token.type === 'field'" class="flex-center flex-column my-1">
					<div class="flex-center flex-column">
						<span
							class="py-2 white--text small-text white-p mb-2 text-center d-flex align-start"
							style="max-width: 100px">
							{{ token.label }}
						</span>
						<div class="flex-center">
							<input
								type="number"
								class="pa-2"
								:style="{
									borderTopRightRadius: token.unit ? '0' : '5px',
									borderBottomRightRadius: token.unit ? '0' : '5px',
									borderTopLeftRadius: '5px',
									borderBottomLeftRadius: '5px',
								}"
								v-model.number="fieldValues[token.field_name]"
								style="background: #fff; width: 85px; margin: 0 5px" />
							<div
								class="text-h5 mx-1 ml-n2 text-medium-emphasis flex-center pa-2"
								v-if="token.unit"
								style="
									background-color: #fff;
									border-top-left-radius: 0 !important;
									border-bottom-left-radius: 0 !important;
									border-top-right-radius: 5px;
									border-bottom-right-radius: 5px;
									height: 48px;
									border-left: 1px solid #11111120;
								">
								{{ token.unit }}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<div
			class="w-100 px-4 py-6 white--text hidden-md-and-up"
			style="border: 1px solid #fefefe30; border-radius: 5px">
			<!-- Input Fields -->
			<div v-for="(field, index) in fields" :key="index" class="mb-4">
				<label class="small-text white-p" style="line-height: normal !important">{{ field.label }}</label>
				<div class="d-flex mt-2">
					<input
						type="number"
						class="pa-2"
						:style="{
							borderTopRightRadius: field.unit ? '0' : '5px',
							borderBottomRightRadius: field.unit ? '0' : '5px',
							borderTopLeftRadius: '5px',
							borderBottomLeftRadius: '5px',
						}"
						v-model.number="fieldValues[field.field_name]"
						style="background: #fff; width: 100%" />
					<div
						v-if="field.unit"
						class="text-h5 text-medium-emphasis flex-center pa-2"
						style="
							background-color: #fff;
							border-top-left-radius: 0 !important;
							border-bottom-left-radius: 0 !important;
							border-top-right-radius: 5px;
							border-bottom-right-radius: 5px;
							border-left: 1px solid #11111120;
						">
						{{ field.unit }}
					</div>
				</div>
			</div>

			<!-- Formula Display -->
			<div class="white--text pb-2 pt-6">
				<span class="text-md-h5">Formula:</span>
				<div class="text-md-h6 mt-2">
					{{ calculator.calculation_formula }}
				</div>
			</div>
		</div>

		<span class="w-100 text-center text-h4 white--text my-5">=</span>
		<div
			class="flex-center mb-15 w-smm-100 py-5 px-md-10 px-6 text-md-h5 text-body-5 white--text"
			style="border: 1px solid #fefefe30; border-radius: 5px">
			{{ result.toFixed(2) }} {{ calculator.result_unit }}
		</div>
	</div>
</template>

<script>
	import { evaluate } from "mathjs"

	export default {
		name: "CalculatorFormula",
		props: {
			calculator: {
				type: Object,
				required: true,
			},
			formula: {
				type: String,
				required: true,
			},
			fields: {
				type: Array,
				required: true,
			},
		},
		data() {
			let fieldValues = {}
			this.fields.forEach((field) => {
				fieldValues[field.field_name] = 0
			})
			return {
				fieldValues: fieldValues,
				tokens: [],
			}
		},
		created() {
			this.parseFormula()
			this.computeResult()
		},
		watch: {
			fieldValues: {
				handler() {
					this.computeResult()
				},
				deep: true,
			},
		},
		computed: {
			result() {
				let expression = this.formula

				// Replace all placeholders with their corresponding values
				this.fields.forEach((field) => {
					const field_name = field.field_name
					let value = parseFloat(this.fieldValues[field_name]) || 0

					// If the unit is '%', divide the value by 100
					if (field.unit === "%") {
						value = value / 100
					}

					expression = expression.replace(new RegExp(`\\{${field_name}\\}`, "g"), value)
				})

				// Check if any placeholders are left in the expression
				if (/\{\w+\}/.test(expression)) {
					console.error("Error: Some placeholders were not replaced properly.")
					return "Error"
				}

				// Evaluate the final expression
				return this.evaluateExpression(expression)
			},
		},

		methods: {
			parseFormula() {
				let formula = this.formula
				let regex = /({\w+})/g
				let tokens = []
				let lastIndex = 0
				let match
				while ((match = regex.exec(formula)) !== null) {
					if (match.index > lastIndex) {
						tokens.push({
							type: "text",
							value: formula.substring(lastIndex, match.index),
						})
					}
					let field_name = match[0].slice(1, -1)
					let field = this.fields.find((f) => f.field_name === field_name)
					if (field) {
						tokens.push({
							type: "field",
							field_name: field_name,
							unit: field.unit,
							label: field.label,
						})
					} else {
						tokens.push({
							type: "text",
							value: match[0],
						})
					}
					lastIndex = match.index + match[0].length
				}
				if (lastIndex < formula.length) {
					tokens.push({
						type: "text",
						value: formula.substring(lastIndex),
					})
				}
				this.tokens = tokens
			},
			computeResult() {
				let expression = this.formula

				// Replace all placeholders with their corresponding values
				this.fields.forEach((field) => {
					const field_name = field.field_name
					let value = parseFloat(this.fieldValues[field_name]) || 0

					// If the unit is '%', divide the value by 100
					if (field.unit === "%") {
						value = value / 100
					}

					// Ensure to replace all occurrences of {field_name}
					expression = expression.replace(new RegExp(`\\{${field_name}\\}`, "g"), value)
				})

				// Check if any placeholders are left in the expression
				if (/\{\w+\}/.test(expression)) {
					console.error("Error: Some placeholders were not replaced properly.")
					this.$emit("update:result", "Error")
					return
				}

				// Evaluate the final expression
				const result = this.evaluateExpression(expression)
				this.$emit("update:result", result)
			},
			evaluateExpression(expression) {
				try {
					return evaluate(expression)
				} catch (e) {
					console.error("Evaluation error:", e)
					return "Error"
				}
			},
		},
	}
</script>
